import React from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion } from 'framer-motion';
import { ArrowRight, LayoutTemplate, Target, BarChart3, Zap, MessageSquareQuote } from 'lucide-react';
export function BuildCard({
  state,
  updateState,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const color = SPECIALTY_COLORS[role];
  const isReady = state.cardSegmentation.trim().length > 0 && state.cardFeatures.trim().length > 0 && state.cardMotivation.trim().length > 0 && state.cardQuoteForOthers.trim().length > 0;
  const fields = [{
    key: 'cardSegmentation' as const,
    icon: Target,
    letter: 'A',
    label: 'Segmentation & Targeting',
    sublabel: 'How should we identify and prioritise this segment?',
    placeholder: `e.g. Focus on ${role}s who... / Prioritise those with... / Key signals to look for are...`
  }, {
    key: 'cardFeatures' as const,
    icon: BarChart3,
    letter: 'B',
    label: 'Features, Arguments & Data',
    sublabel: 'Which product features and evidence resonate most with this specialty?',
    placeholder: `e.g. Lead with... data / Emphasise the... benefit / They respond to... evidence`
  }, {
    key: 'cardMotivation' as const,
    icon: Zap,
    letter: 'C',
    label: 'The Right Motivation',
    sublabel: 'What framing or message drives this specialty to act?',
    placeholder: `e.g. Frame it as... / Their core motivation is... / They need to feel...`
  }, {
    key: 'cardQuoteForOthers' as const,
    icon: MessageSquareQuote,
    letter: '→',
    label: 'What Other Groups Should Know',
    sublabel: 'A key takeaway or provocative insight to share with the room.',
    placeholder: `The one thing everyone should understand about ${role}s is...`
  }];
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto pt-20" data-id="element-330">
      <div className="max-w-4xl w-full mx-auto" data-id="element-331">
        <div className="text-center mb-10" data-id="element-332">
          <div className="inline-flex items-center justify-center p-3 bg-brand/10 text-brand rounded-full mb-4" data-id="element-333">
            <LayoutTemplate size={28} data-id="element-334" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-dark mb-4" data-id="element-335">
            Build Your Group's Strategy Card
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-336">
            Synthesise what you've learned into actionable recommendations.
            You'll present this to the other groups.
          </p>
        </div>

        <div className="bg-white rounded-3xl p-8 border-2 shadow-sm mb-10" style={{
        borderColor: `${color}30`
      }} data-id="element-337">
          <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100" data-id="element-338">
            <div data-id="element-339">
              <div className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-340">
                Group
              </div>
              <div className="text-xl font-bold text-dark" data-id="element-341">
                {state.groupName || 'Your Group'}
              </div>
            </div>
            <div className="text-right" data-id="element-342">
              <div className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-343">
                Specialty
              </div>
              <div className="text-xl font-bold" style={{
              color
            }} data-id="element-344">
                {role}s
              </div>
            </div>
          </div>

          <div className="space-y-8" data-id="element-345">
            {fields.map(field => {
            const Icon = field.icon;
            return <div key={field.key} data-id="element-346">
                  <div className="flex items-center mb-3" data-id="element-347">
                    <div className="w-7 h-7 rounded-md bg-brand/10 text-brand flex items-center justify-center mr-3 flex-shrink-0" data-id="element-348">
                      <Icon size={16} data-id="element-349" />
                    </div>
                    <div data-id="element-350">
                      <span className="text-xs font-bold text-brand uppercase tracking-wider mr-2" data-id="element-351">
                        {field.letter !== '→' ? `Objective ${field.letter}` : 'Share-back'}
                      </span>
                      <span className="text-sm font-bold text-gray-800 uppercase tracking-wider" data-id="element-352">
                        {field.label}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mb-2 ml-10" data-id="element-353">
                    {field.sublabel}
                  </p>
                  <textarea value={state[field.key]} onChange={e => updateState({
                [field.key]: e.target.value
              })} placeholder={field.placeholder} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-brand focus:ring-0 transition-colors outline-none resize-none h-28 ml-0" data-id="element-354" />
                </div>;
          })}
          </div>
        </div>

        <div className="flex justify-center pb-8 h-16" data-id="element-355">
          <motion.button initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} onClick={nextStep} disabled={!isReady} className={`inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-full transition-colors shadow-lg ${isReady ? 'bg-dark hover:bg-black' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-356">
            Preview Our Card
            <ArrowRight className="ml-2 w-5 h-5" data-id="element-357" />
          </motion.button>
        </div>
      </div>
    </div>;
}